import styled from 'styled-components';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { useIntl } from 'react-intl';
import { DownloadMenu } from '../application/download-menu';
import { RefreshIcon } from '../../shared-components/icons/refresh-icon';
import { refreshCustomerList, filterCustomers } from '@algoan/dashboard/store/thunks';
import { initialCustomerFilter } from '@algoan/dashboard/store/slice';
import { filterSelector } from '@algoan/dashboard/store/selectors';
import { isCustomerListLoadingSelector, loadableCustomerNumberSelector } from './customer-table-selectors';
import { allTeamsValue } from '@algoan/dashboard/env';
import { messages } from '../application/application-messages';
import { addAlpha } from '@algoan/deep-dive/theme';
import { Button } from '../../shared-components/atomic-components';

export const ActionSection: React.FC = () => {
  const areCustomersFetching = useSelector(isCustomerListLoadingSelector);
  const loadableCustomerNumber = useSelector(loadableCustomerNumberSelector);
  const filterData = useSelector(filterSelector);
  const dispatch = useDispatch();
  const intl = useIntl();

  const showAllApplicationsButton =
    !areCustomersFetching &&
    (filterData.inputValue ||
      filterData.hasAnalysis !== undefined ||
      filterData.states.length > 0 ||
      filterData.team !== allTeamsValue);

  return (
    <S.Container>
      <S.LeftSide>
        <S.ApplicationsNumber>
          {areCustomersFetching
            ? ''
            : intl.formatMessage(messages.numberOfApplications, {
                number: `${loadableCustomerNumber}`,
              })}
        </S.ApplicationsNumber>
        {showAllApplicationsButton && (
          <>
            <S.Divider />
            <S.AllApplications
              onClick={() => {
                dispatch(filterCustomers(initialCustomerFilter));
              }}
            >
              {intl.formatMessage(messages.allApplications)}
            </S.AllApplications>
          </>
        )}
      </S.LeftSide>

      <S.RightSide>
        <DownloadMenu />
        <S.RefreshButton
          variant="tertiary"
          onClick={() => dispatch(refreshCustomerList())}
          data-testid="refresh-button"
          aria-label="Refresh"
        >
          <RefreshIcon />
        </S.RefreshButton>
      </S.RightSide>
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    align-items: self-end;
  `,
  ApplicationsNumber: styled.div`
    display: flex;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    color: ${(props) => props.theme.secondary.softBlue3};
    align-self: end;
    margin: 0px 16px 4px 10px;
  `,
  AllApplications: styled.div`
    display: flex;
    font-size: 10px;
    font-weight: 600;
    color: ${(props) => props.theme.secondary.softBlue1};
    align-self: end;
    text-transform: uppercase;
    margin-bottom: 4px;
    margin-left: 16px;
    cursor: pointer;
    &:hover {
      color: ${({ theme }) => theme.secondary.softBlue2};
    }
  `,
  LeftSide: styled.div`
    display: flex;
  `,
  Divider: styled.div`
    background: ${(props) => addAlpha(props.theme.algoanBlue, 0.1)};
    width: 1px;
    height: 16px;
  `,
  RightSide: styled.div`
    display: flex;
    flex-direction: row;
  `,
  RefreshButton: styled(Button)`
    margin-left: 10px;
    background-color: ${(props) => props.theme.secondary.chalk};
    border-radius: 4px;
    border: unset;
    padding: 9px 9px;
    height: 32px;
    width: 32px;
    &:hover {
      box-shadow: ${(props) => addAlpha(props.theme.algoanBlue, 0.15)};
    }
  `,
};
